import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Check, Star, Shield, Clock, Users, FileText, Car, 
  Building2, Briefcase, Phone, Zap, Award, Crown, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';

export default function ServicePackages() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [expandedPackage, setExpandedPackage] = useState(null);

  const packages = [
    {
      id: 'basic',
      name: isAr ? 'الباقة الأساسية' : 'Basic Package',
      price: '500',
      period: isAr ? 'شهرياً' : 'monthly',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      icon: Star,
      description: isAr 
        ? 'مثالية للأفراد الذين يحتاجون خدمات حكومية محدودة'
        : 'Perfect for individuals with limited government service needs',
      features: [
        { text: isAr ? 'تجديد الهوية الإماراتية' : 'Emirates ID Renewal', included: true },
        { text: isAr ? 'تجديد رخصة القيادة' : 'Driving License Renewal', included: true },
        { text: isAr ? 'خدمات الهجرة الأساسية' : 'Basic Immigration Services', included: true },
        { text: isAr ? '5 معاملات شهرياً' : '5 Transactions Monthly', included: true },
        { text: isAr ? 'دعم عبر الواتساب' : 'WhatsApp Support', included: true },
        { text: isAr ? 'تجديد تسجيل المركبات' : 'Vehicle Registration Renewal', included: false },
        { text: isAr ? 'خدمات الشركات' : 'Business Services', included: false },
        { text: isAr ? 'مندوب مخصص' : 'Dedicated Representative', included: false },
      ],
      benefits: [
        isAr ? 'توفير الوقت والجهد' : 'Save time and effort',
        isAr ? 'تتبع المعاملات إلكترونياً' : 'Track transactions online',
        isAr ? 'إشعارات فورية بالتحديثات' : 'Instant update notifications',
      ]
    },
    {
      id: 'premium',
      name: isAr ? 'الباقة المتميزة' : 'Premium Package',
      price: '1,200',
      period: isAr ? 'شهرياً' : 'monthly',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      icon: Crown,
      popular: true,
      description: isAr 
        ? 'الأكثر طلباً - مثالية للعائلات وأصحاب المركبات'
        : 'Most Popular - Ideal for families and vehicle owners',
      features: [
        { text: isAr ? 'جميع مميزات الباقة الأساسية' : 'All Basic Package Features', included: true },
        { text: isAr ? 'تجديد تسجيل المركبات' : 'Vehicle Registration Renewal', included: true },
        { text: isAr ? 'خدمات الفحص الفني' : 'Technical Inspection Services', included: true },
        { text: isAr ? 'معاملات المرور' : 'Traffic Department Services', included: true },
        { text: isAr ? '15 معاملة شهرياً' : '15 Transactions Monthly', included: true },
        { text: isAr ? 'أولوية في التنفيذ' : 'Priority Processing', included: true },
        { text: isAr ? 'خدمات الشركات' : 'Business Services', included: false },
        { text: isAr ? 'مندوب مخصص' : 'Dedicated Representative', included: false },
      ],
      benefits: [
        isAr ? 'تغطية شاملة للمركبات' : 'Complete vehicle coverage',
        isAr ? 'خدمة أسرع بنسبة 50%' : '50% faster service',
        isAr ? 'دعم على مدار الساعة' : '24/7 support',
      ]
    },
    {
      id: 'business',
      name: isAr ? 'باقة الأعمال' : 'Business Package',
      price: '2,500',
      period: isAr ? 'شهرياً' : 'monthly',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      icon: Briefcase,
      description: isAr 
        ? 'حل شامل للشركات الصغيرة والمتوسطة'
        : 'Complete solution for small and medium businesses',
      features: [
        { text: isAr ? 'جميع مميزات الباقة المتميزة' : 'All Premium Package Features', included: true },
        { text: isAr ? 'تجديد الرخصة التجارية' : 'Trade License Renewal', included: true },
        { text: isAr ? 'تأشيرات الموظفين' : 'Employee Visas', included: true },
        { text: isAr ? 'عقود العمل' : 'Employment Contracts', included: true },
        { text: isAr ? 'معاملات غير محدودة' : 'Unlimited Transactions', included: true },
        { text: isAr ? 'مدير حساب مخصص' : 'Dedicated Account Manager', included: true },
        { text: isAr ? 'تقارير شهرية' : 'Monthly Reports', included: true },
        { text: isAr ? 'استشارات قانونية' : 'Legal Consultations', included: true },
      ],
      benefits: [
        isAr ? 'إدارة كاملة لشؤون الموظفين' : 'Complete employee management',
        isAr ? 'توفير تكاليف المندوبين الداخليين' : 'Save on internal PRO costs',
        isAr ? 'ضمان الامتثال القانوني' : 'Legal compliance guaranteed',
      ]
    },
    {
      id: 'enterprise',
      name: isAr ? 'باقة المؤسسات' : 'Enterprise Package',
      price: isAr ? 'حسب الطلب' : 'Custom',
      period: '',
      color: 'from-slate-700 to-slate-900',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-300',
      icon: Building2,
      description: isAr 
        ? 'حلول مخصصة للشركات الكبرى والمجموعات'
        : 'Custom solutions for large corporations and groups',
      features: [
        { text: isAr ? 'جميع مميزات باقة الأعمال' : 'All Business Package Features', included: true },
        { text: isAr ? 'فريق مخصص للخدمات' : 'Dedicated Service Team', included: true },
        { text: isAr ? 'تكامل مع أنظمة الشركة' : 'Integration with Company Systems', included: true },
        { text: isAr ? 'خدمات VIP' : 'VIP Services', included: true },
        { text: isAr ? 'استشارات استراتيجية' : 'Strategic Consultations', included: true },
        { text: isAr ? 'تدريب الموظفين' : 'Employee Training', included: true },
        { text: isAr ? 'SLA مخصص' : 'Custom SLA', included: true },
        { text: isAr ? 'مدير علاقات كبار العملاء' : 'Key Account Manager', included: true },
      ],
      benefits: [
        isAr ? 'حلول مصممة خصيصاً' : 'Tailor-made solutions',
        isAr ? 'أولوية قصوى في جميع المعاملات' : 'Highest priority on all transactions',
        isAr ? 'دعم على مستوى المؤسسة' : 'Enterprise-level support',
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: Clock,
      title: isAr ? 'توفير الوقت' : 'Save Time',
      description: isAr ? 'لا مزيد من الانتظار في الطوابير - ننجز معاملاتك بينما تركز على عملك' : 'No more waiting in queues - we handle your transactions while you focus on your work'
    },
    {
      icon: Users,
      title: isAr ? 'فريق محترف' : 'Professional Team',
      description: isAr ? 'مندوبون معتمدون بخبرة أكثر من 10 سنوات في الخدمات الحكومية' : 'Certified PROs with 10+ years of experience in government services'
    },
    {
      icon: Shield,
      title: isAr ? 'ضمان الإنجاز' : 'Guaranteed Completion',
      description: isAr ? 'نضمن إتمام معاملاتك في الوقت المحدد أو استرداد المبلغ' : 'We guarantee completion on time or your money back'
    },
    {
      icon: Zap,
      title: isAr ? 'سرعة التنفيذ' : 'Fast Processing',
      description: isAr ? 'إنجاز المعاملات بأسرع وقت ممكن بفضل علاقاتنا المميزة' : 'Fastest processing times thanks to our excellent relationships'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#334155] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#1E9BD9] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#F5A962] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {isAr ? 'باقات تخليص الخدمات الحكومية' : 'Government Services Packages'}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {isAr 
                ? 'وفّر وقتك وجهدك - نحن نتولى جميع معاملاتك الحكومية بكفاءة واحترافية'
                : 'Save your time and effort - we handle all your government transactions efficiently and professionally'}
            </p>
            <div className="flex items-center justify-center gap-4 text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-400" />
                <span>{isAr ? 'بدون عقود طويلة' : 'No long contracts'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-400" />
                <span>{isAr ? 'إلغاء في أي وقت' : 'Cancel anytime'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-400" />
                <span>{isAr ? 'ضمان استرداد المال' : 'Money-back guarantee'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-10 relative z-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => {
            const IconComponent = pkg.icon;
            const isExpanded = expandedPackage === pkg.id;
            
            return (
              <div 
                key={pkg.id}
                className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                  pkg.popular ? 'border-purple-400 scale-105' : pkg.borderColor
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-bold px-4 py-1 rounded-full">
                      {isAr ? 'الأكثر طلباً' : 'Most Popular'}
                    </span>
                  </div>
                )}
                
                <div className="p-6">
                  {/* Header */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${pkg.color} flex items-center justify-center mb-4`}>
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">{pkg.name}</h3>
                  <p className="text-sm text-[#64748B] mb-4">{pkg.description}</p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-[#0F172A]">{pkg.price}</span>
                    {pkg.period && (
                      <span className="text-[#64748B] text-sm ms-1">
                        {isAr ? 'درهم' : 'AED'} / {pkg.period}
                      </span>
                    )}
                  </div>
                  
                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {pkg.features.slice(0, isExpanded ? undefined : 4).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-gray-200 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${feature.included ? 'text-[#0F172A]' : 'text-[#94A3B8]'}`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {pkg.features.length > 4 && (
                    <button
                      onClick={() => setExpandedPackage(isExpanded ? null : pkg.id)}
                      className="flex items-center gap-1 text-[#1E9BD9] text-sm font-medium mb-4 hover:underline"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="h-4 w-4" />
                          {isAr ? 'عرض أقل' : 'Show Less'}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          {isAr ? 'عرض المزيد' : 'Show More'}
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* CTA Button */}
                  <Button 
                    className={`w-full bg-gradient-to-r ${pkg.color} hover:opacity-90 text-white`}
                    size="lg"
                  >
                    {pkg.id === 'enterprise' 
                      ? (isAr ? 'تواصل معنا' : 'Contact Us')
                      : (isAr ? 'اشترك الآن' : 'Subscribe Now')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">
              {isAr ? 'لماذا تختار CloudGM؟' : 'Why Choose CloudGM?'}
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              {isAr 
                ? 'نوفر عليك عناء التعامل مع الدوائر الحكومية ونضمن لك إنجاز معاملاتك بسرعة ودقة'
                : 'We save you the hassle of dealing with government departments and guarantee fast and accurate completion'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#1E9BD9]/10 to-[#F5A962]/10 rounded-2xl flex items-center justify-center">
                    <IconComponent className="h-8 w-8 text-[#1E9BD9]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#64748B]">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Included */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">
            {isAr ? 'الخدمات التي نقدمها' : 'Services We Provide'}
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: FileText, title: isAr ? 'خدمات الهوية والإقامة' : 'ID & Residency Services', items: [
              isAr ? 'تجديد الهوية الإماراتية' : 'Emirates ID Renewal',
              isAr ? 'تجديد الإقامة' : 'Residence Visa Renewal',
              isAr ? 'تغيير الكفالة' : 'Sponsorship Transfer',
              isAr ? 'تصاريح الدخول' : 'Entry Permits'
            ]},
            { icon: Car, title: isAr ? 'خدمات المركبات' : 'Vehicle Services', items: [
              isAr ? 'تجديد ملكية المركبة' : 'Vehicle Registration Renewal',
              isAr ? 'الفحص الفني' : 'Technical Inspection',
              isAr ? 'نقل الملكية' : 'Ownership Transfer',
              isAr ? 'تجديد رخصة القيادة' : 'Driving License Renewal'
            ]},
            { icon: Briefcase, title: isAr ? 'خدمات الشركات' : 'Business Services', items: [
              isAr ? 'تجديد الرخصة التجارية' : 'Trade License Renewal',
              isAr ? 'تأشيرات العمل' : 'Work Permits',
              isAr ? 'عقود العمل' : 'Employment Contracts',
              isAr ? 'تصاريح البلدية' : 'Municipality Permits'
            ]}
          ].map((category, idx) => {
            const IconComponent = category.icon;
            return (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E2E8F0]">
                <div className="w-12 h-12 bg-[#1E9BD9]/10 rounded-xl flex items-center justify-center mb-4">
                  <IconComponent className="h-6 w-6 text-[#1E9BD9]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-center gap-2 text-[#64748B]">
                      <Check className="h-4 w-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {isAr ? 'هل أنت مستعد لتوفير وقتك؟' : 'Ready to Save Your Time?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {isAr 
              ? 'تواصل معنا اليوم واحصل على استشارة مجانية لاختيار الباقة المناسبة'
              : 'Contact us today and get a free consultation to choose the right package'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+971501511917">
              <Button size="lg" className="bg-white text-[#1E9BD9] hover:bg-gray-100 text-lg px-8">
                <Phone className="h-5 w-5 me-2" />
                +971 50 151 1917
              </Button>
            </a>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8">
                {isAr ? 'سجل الآن' : 'Register Now'}
                <ArrowRight className="h-5 w-5 ms-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
